// frontend/src/components/Dashboard/AIStatsCard.jsx - CLEANED
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faClock,
  faDollarSign,
  faStar,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import API from "../../api";

const AIStatsCard = ({ refreshInterval = 30000 }) => {
  const [aiStats, setAiStats] = useState({
    stats: {
      totalConversations: 0,
      monthConversations: 0,
      averageRating: null,
      monthlyCap: 1000,
      currentMonthUsage: 0,
      remaining: 1000,
      enabled: true,
      totalCost: 0,
    },
    recentConversations: [],
    dailyData: [],
    settings: {},
  });
  const [loading, setLoading] = useState(true);

  const fetchAIStats = async () => {
    try {
      setLoading(true);
      const response = await API.get("/dashboard/ai-stats");
      if (response.data.success) {
        setAiStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching AI stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAIStats();
    const interval = setInterval(fetchAIStats, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  if (loading) {
    return (
      <div className="ai-stats-card loading">
        <div className="ai-stats-skeleton">
          <div className="skeleton-header" />
          <div className="skeleton-grid">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const usagePercentage = Math.min(
    (aiStats.stats.currentMonthUsage / aiStats.stats.monthlyCap) * 100,
    100,
  );
  const isWarning = usagePercentage > 80;

  return (
    <div className="ai-stats-card">
      <div className="ai-stats-header">
        <div className="ai-stats-title">
          <FontAwesomeIcon icon={faRobot} className="ai-icon" />
          <h3>AI Assistant Analytics</h3>
          <span
            className={`ai-status ${aiStats.stats.enabled ? "enabled" : "disabled"}`}
          >
            {aiStats.stats.enabled ? "Active" : "Disabled"}
          </span>
        </div>
        <span className="ai-usage-badge">
          {aiStats.stats.currentMonthUsage} / {aiStats.stats.monthlyCap}{" "}
          conversations
        </span>
      </div>

      <div className="ai-stats-grid">
        <div className="ai-stat-card">
          <div className="ai-stat-value">
            {aiStats.stats.totalConversations}
          </div>
          <div className="ai-stat-label">Total Conversations</div>
        </div>
        <div className="ai-stat-card">
          <div className="ai-stat-value">
            {aiStats.stats.monthConversations}
          </div>
          <div className="ai-stat-label">This Month</div>
        </div>
        <div className="ai-stat-card">
          <div className="ai-stat-value">
            {aiStats.stats.averageRating
              ? `${aiStats.stats.averageRating.toFixed(1)}⭐`
              : "N/A"}
          </div>
          <div className="ai-stat-label">Avg Rating</div>
        </div>
        <div className="ai-stat-card">
          <div className="ai-stat-value">
            $
            {aiStats.stats.totalCost
              ? aiStats.stats.totalCost.toFixed(2)
              : "0.00"}
          </div>
          <div className="ai-stat-label">Total API Cost</div>
        </div>
        <div className="ai-stat-card">
          <div className="ai-stat-value">{aiStats.stats.remaining}</div>
          <div className="ai-stat-label">Remaining This Month</div>
        </div>
      </div>

      <div className="ai-usage-bar">
        <div className="ai-usage-label">
          <span>Monthly Usage</span>
          <span className={isWarning ? "warning" : ""}>
            {Math.round(usagePercentage)}%
          </span>
        </div>
        <div className="ai-usage-track">
          <div
            className={`ai-usage-fill ${isWarning ? "warning" : ""}`}
            style={{ width: `${usagePercentage}%` }}
          />
        </div>
      </div>

      {aiStats.recentConversations.length > 0 && (
        <div className="ai-recent-conversations">
          <h4>Recent Conversations</h4>
          <div className="ai-conversation-list">
            {aiStats.recentConversations.slice(0, 5).map((conv, index) => (
              <div key={index} className="ai-conversation-item">
                <div className="conv-info">
                  <span className="conv-date">
                    {new Date(conv.date).toLocaleDateString()}
                  </span>
                  <span className="conv-messages">
                    {conv.messageCount} messages
                  </span>
                </div>
                <div className="conv-meta">
                  {conv.rating && (
                    <span className="conv-rating">
                      {"⭐".repeat(Math.round(conv.rating))}
                    </span>
                  )}
                  <span className="conv-cost">${conv.cost.toFixed(3)}</span>
                  <span
                    className={`conv-status ${conv.resolved ? "resolved" : "open"}`}
                  >
                    {conv.resolved ? "✓" : "⏳"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIStatsCard;
